/**
 * This package contains utility functions for working with web asset templates.
 *
 * When a Web asset is built on a template, it needs to store the template id and other variables
 * that are used in the template inside the template. These functions help in embedding and
 * extracting that data.
 */

/**
 * Returns an HTML document by parsing an HTML string.
 * @param htmlStr
 * @returns {Document}
 */
export const strToDom = (htmlStr) => {
  const parser = new DOMParser();

  return parser.parseFromString(htmlStr, 'text/html');
};

/**
 * Checks if the provided HTML document has metadata that marks it as a template.
 * @param doc {Document}
 * @returns {boolean}
 */
const isTemplateDocument = (doc) =>
  !!doc.querySelector('head').dataset.template;

/**
 * Checks if the provided HTML string has metadata that marks it as a template.
 * @param htmlStr
 * @returns {boolean}
 */
export const isTemplateString = (htmlStr) =>
  isTemplateDocument(strToDom(htmlStr));

/**
 * Returns a script element with the provided object embedded as a JSON script.
 * @param obj
 * @returns {Element}
 */
export const createJsonScriptFromObj = (obj) => {
  const jsonData = JSON.stringify(obj);
  const scriptTag = document.createElement('script');

  scriptTag.setAttribute('type', 'application/json');
  scriptTag.setAttribute('id', 'variables');
  scriptTag.appendChild(document.createTextNode(jsonData));

  return scriptTag;
};

/**
 * Embeds the template id and variable data provided into the provided html string.
 * @param htmlStr
 * @param templateId
 * @param vars
 * @return {string}
 */
export const addTemplateMetadata = (htmlStr, templateId, vars) => {
  const doc = strToDom(htmlStr);
  const docHead = doc.querySelector('head');

  docHead.dataset.template = templateId;
  docHead.appendChild(createJsonScriptFromObj(vars));

  return doc.documentElement.outerHTML;
};

/**
 * Extracts the template id and variable metadata from the html string provided.
 * @param htmlStr
 * @return {*}
 */
export const getTemplateMetadata = (htmlStr) => {
  const docHead = strToDom(htmlStr).querySelector('head');

  if (!docHead) return {id: null, values: {}};
  const templateId = docHead.dataset.template;
  const varScript = docHead.querySelector('script#variables');

  if (!varScript) return {id: null, values: {}};
  const values = JSON.parse(varScript.text);

  return {
    id: templateId,
    values,
  };
};



// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./src/utils/template.js